import { useState, useEffect, useCallback, useRef } from "react";
import ReactFlow, {
  Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow,
  Handle, Position, MarkerType, ReactFlowProvider,
  BackgroundVariant, BaseEdge, EdgeLabelRenderer, getBezierPath,
} from "reactflow";
import "reactflow/dist/style.css";

// ─── Constants ───────────────────────────────────────────────
const API = process.env.REACT_APP_API_BASE || "http://localhost:3001/api";
function getToken() { return localStorage.getItem("wa_token"); }
async function wfFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}`, ...opts.headers },
  });
  return res.json();
}

// Client-side cycle detection: returns true if adding edge (src→tgt) would create a cycle
function wouldCreateCycle(nodes, edges, srcId, tgtId) {
  const adj = {};
  nodes.forEach(n => { adj[n.id] = []; });
  edges.forEach(e => { if (adj[e.source]) adj[e.source].push(e.target); });
  if (!adj[srcId]) adj[srcId] = [];
  adj[srcId].push(tgtId);
  const visited = new Set(), inStack = new Set();
  function dfs(id) {
    visited.add(id); inStack.add(id);
    for (const next of (adj[id] || [])) {
      if (!visited.has(next)) { if (dfs(next)) return true; }
      else if (inStack.has(next)) return true;
    }
    inStack.delete(id); return false;
  }
  for (const id of Object.keys(adj)) {
    if (!visited.has(id)) { if (dfs(id)) return true; }
  }
  return false;
}

const NODE_META = {
  start:     { label: "Start",         icon: "▶", color: "#10b981" },
  delay:     { label: "Wait / Delay",  icon: "⏱", color: "#f59e0b" },
  message:   { label: "Send Message",  icon: "💬", color: "#6366f1" },
  condition: { label: "If / Else",     icon: "⚡", color: "#8b5cf6" },
  batch:     { label: "Batch Split",   icon: "⊞", color: "#f97316" },
  action:    { label: "Action",        icon: "⚙", color: "#06b6d4" },
  end:       { label: "End",           icon: "⏹", color: "#64748b" },
};

const PALETTE_NODES = ["delay", "message", "condition", "batch", "action", "end"];

function getDefaultData(type) {
  switch (type) {
    case "start":     return { label: "Start Enrollment", triggerType: "hubspot_list", triggerEvent: "delivered", triggerTemplate: "" };
    case "delay":     return { label: "Wait", amount: 1, unit: "hours" };
    case "message":   return { label: "Send Message", template: null, variableMapping: {}, headerMediaUrl: "", maxRetries: 0, retryDelayMinutes: 30 };
    case "condition": return { label: "If / Else", conditionGroups: [], maxWaitMinutes: 0 };
    case "action":    return { label: "Action", actionType: "end_workflow", property: "", value: "", leadPipeline: "", leadStage: "", leadOwner: "", dealPipeline: "", dealStage: "", dealOwner: "" };
    case "batch":     return { label: "Batch Split", batchCount: 2 };
    case "end":       return { label: "End" };
    default:          return { label: type };
  }
}

const STATUS_COLORS = { draft: "#94a3b8", active: "#10b981", paused: "#f59e0b", completed: "#6366f1", archived: "#64748b" };

// ─── Shared styles ────────────────────────────────────────────
const card = (extra = {}) => ({
  background: "#fff", borderRadius: 12, padding: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,.07)", ...extra
});
const inp = { width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
const btn = (color = "#6366f1", extra = {}) => ({
  background: color, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px",
  fontSize: 13, fontWeight: 600, cursor: "pointer", ...extra
});
const label = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 5 };

// ─── Node Components ──────────────────────────────────────────
const nodeBox = (color, selected) => ({
  background: "#fff",
  border: `2px solid ${selected ? color : "#e2e8f0"}`,
  borderRadius: 12, minWidth: 200,
  boxShadow: selected ? `0 0 0 4px ${color}22, 0 2px 8px rgba(0,0,0,.1)` : "0 2px 8px rgba(0,0,0,.08)",
  fontFamily: "Inter, sans-serif", overflow: "hidden",
});
const nodeHeader = (color) => ({ background: color, color: "#fff", padding: "8px 12px", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 });
const nodeBody = { padding: "10px 12px", fontSize: 12, color: "#475569", lineHeight: 1.5 };

function StartNode({ data, selected }) {
  const c = NODE_META.start.color;
  const isEvent = data.triggerType === "message_event";
  const EVENT_LABELS = { sent: "Sent", delivered: "Delivered", read: "Read", clicked: "Link Clicked", replied: "WhatsApp Replied", opt_out: "OPT Out" };
  return (
    <div style={nodeBox(c, selected)}>
      <div style={nodeHeader(c)}><span>▶</span> {data.label || "Start"}</div>
      <div style={nodeBody}>
        {isEvent ? (
          <span style={{ color: "#f59e0b", fontWeight: 700 }}>
            ⚡ On {EVENT_LABELS[data.triggerEvent] || data.triggerEvent || "—"}
            {data.triggerTemplate ? ` · ${data.triggerTemplate}` : ""}
          </span>
        ) : (
          data.audienceCount != null
            ? <span style={{ color: "#10b981", fontWeight: 700 }}>{data.audienceCount.toLocaleString()} contacts enrolled</span>
            : <span style={{ color: "#94a3b8" }}>No audience selected</span>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: c, width: 12, height: 12 }} />
    </div>
  );
}

function DelayNode({ data, selected }) {
  const c = NODE_META.delay.color;
  return (
    <div style={nodeBox(c, selected)}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>⏱</span> {data.label || "Wait"}</div>
      <div style={nodeBody}>{data.amount || 1} {data.unit || "hours"}</div>
      <Handle type="source" position={Position.Bottom} style={{ background: c, width: 12, height: 12 }} />
    </div>
  );
}

function MessageNode({ data, selected }) {
  const c = NODE_META.message.color;
  return (
    <div style={nodeBox(c, selected)}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>💬</span> {data.label || "Send Message"}</div>
      <div style={nodeBody}>
        {data.template
          ? <span style={{ fontWeight: 600, color: "#0f172a" }}>{data.template.name}</span>
          : <span style={{ color: "#94a3b8" }}>No template selected</span>}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: c, width: 12, height: 12 }} />
    </div>
  );
}

const GROUP_COLORS = ["#8b5cf6","#06b6d4","#10b981","#f59e0b","#ef4444","#6366f1","#ec4899","#14b8a6"];
const BATCH_COLORS = ["#f97316","#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#ec4899","#14b8a6","#84cc16"];

function ConditionNode({ data, selected }) {
  const c = NODE_META.condition.color;
  const groups = data.conditionGroups;
  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const totalHandles = hasGroups ? groups.length + 1 : 2;
  const hLeft = (i, tot) => `${Math.round(((i + 1) / (tot + 1)) * 100)}%`;

  return (
    <div style={{ ...nodeBox(c, selected), minWidth: hasGroups ? Math.max(200, groups.length * 80) : 200 }}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>⚡</span> {data.label || "If / Else"}</div>
      <div style={nodeBody}>
        {hasGroups
          ? `${groups.length} group(s) + default`
          : data.conditions?.length
            ? `${data.conditions.length} condition(s) · ${data.logic}`
            : <span style={{ color: "#94a3b8" }}>No conditions configured</span>}
      </div>
      <div style={{ display: "flex", padding: "2px 6px 8px", fontSize: 9, fontWeight: 700 }}>
        {hasGroups ? (
          <>
            {groups.map((g, i) => (
              <span key={g.id} style={{ color: GROUP_COLORS[i % GROUP_COLORS.length], flex: 1, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                ▼ {g.label || `G${i+1}`}
              </span>
            ))}
            <span style={{ color: "#64748b", flex: 1, textAlign: "center" }}>▼ Default</span>
          </>
        ) : (
          <>
            <span style={{ color: "#10b981", flex: 1, textAlign: "center" }}>▼ YES</span>
            <span style={{ color: "#ef4444", flex: 1, textAlign: "center" }}>NO ▼</span>
          </>
        )}
      </div>
      {hasGroups ? (
        <>
          {groups.map((g, i) => (
            <Handle key={g.id} type="source" id={`group_${g.id}`} position={Position.Bottom}
              style={{ left: hLeft(i, totalHandles), background: GROUP_COLORS[i % GROUP_COLORS.length], width: 12, height: 12 }} />
          ))}
          <Handle type="source" id="default" position={Position.Bottom}
            style={{ left: hLeft(groups.length, totalHandles), background: "#64748b", width: 12, height: 12 }} />
        </>
      ) : (
        <>
          <Handle type="source" id="yes" position={Position.Bottom} style={{ left: "28%", background: "#10b981", width: 12, height: 12 }} />
          <Handle type="source" id="no"  position={Position.Bottom} style={{ left: "72%", background: "#ef4444", width: 12, height: 12 }} />
        </>
      )}
    </div>
  );
}

function ActionNode({ data, selected }) {
  const c = NODE_META.action.color;
  const lbl = {
    end_workflow:        "End Workflow",
    update_property:     data.property ? `Update: ${data.property}` : "Update HS Property",
    add_tag:             "Add Tag",
    remove_tag:          "Remove Tag",
    hs_create_lead:      "Create HS Lead",
    hs_create_deal:      "Create HS Deal",
    hs_add_to_list:      data.listName ? `Add to: ${data.listName}` : "Add to HS List",
    hs_remove_from_list: data.listName ? `Remove from: ${data.listName}` : "Remove from HS List",
  };
  return (
    <div style={nodeBox(c, selected)}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>⚙</span> {data.label || "Action"}</div>
      <div style={nodeBody}>{lbl[data.actionType] || data.actionType || "Configure →"}</div>
      <Handle type="source" position={Position.Bottom} style={{ background: c, width: 12, height: 12 }} />
    </div>
  );
}

function EndNode({ data, selected }) {
  const c = NODE_META.end.color;
  return (
    <div style={nodeBox(c, selected)}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>⏹</span> {data.label || "End"}</div>
    </div>
  );
}

function BatchNode({ data, selected }) {
  const c = NODE_META.batch.color;
  const batchCount = Math.max(2, Math.min(10, parseInt(data.batchCount) || 2));
  const hLeft = (i, tot) => `${Math.round(((i + 1) / (tot + 1)) * 100)}%`;
  return (
    <div style={{ ...nodeBox(c, selected), minWidth: Math.max(200, batchCount * 72) }}>
      <Handle type="target" position={Position.Top} style={{ background: c, width: 12, height: 12 }} />
      <div style={nodeHeader(c)}><span>⊞</span> {data.label || "Batch Split"}</div>
      <div style={nodeBody}>Split into {batchCount} equal batch{batchCount !== 1 ? "es" : ""}</div>
      <div style={{ display: "flex", padding: "2px 6px 8px", fontSize: 9, fontWeight: 700 }}>
        {Array.from({ length: batchCount }, (_, i) => (
          <span key={i} style={{ color: BATCH_COLORS[i % BATCH_COLORS.length], flex: 1, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            ▼ Batch {i + 1}
          </span>
        ))}
      </div>
      {Array.from({ length: batchCount }, (_, i) => (
        <Handle key={i} type="source" id={`batch_${i}`} position={Position.Bottom}
          style={{ left: hLeft(i, batchCount), background: BATCH_COLORS[i % BATCH_COLORS.length], width: 12, height: 12 }} />
      ))}
    </div>
  );
}

const nodeTypes = { start: StartNode, delay: DelayNode, message: MessageNode, condition: ConditionNode, action: ActionNode, batch: BatchNode, end: EndNode };

// ─── Deletable Edge ───────────────────────────────────────────
function DeletableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, label, labelStyle, selected }) {
  const [hovered, setHovered] = useState(false);
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const { setEdges } = useReactFlow();

  const onDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEdges(eds => eds.filter(ed => ed.id !== id));
  };

  const showBtn = hovered || selected;
  const edgeColor = style?.stroke || "#6366f1";

  return (
    <>
      {/* Invisible wide stroke for easier hover detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ cursor: "pointer" }}
      />
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        {label && (
          <div style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 10, fontWeight: 700,
            color: edgeColor,
            background: "#fff",
            padding: "1px 6px",
            borderRadius: 4,
            pointerEvents: "none",
            border: `1px solid ${edgeColor}44`,
          }} className="nodrag nopan">
            {label}
          </div>
        )}
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${label ? labelY - 18 : labelY}px)`,
            pointerEvents: "all",
            opacity: showBtn ? 1 : 0,
            transition: "opacity .15s",
          }}
          className="nodrag nopan"
        >
          <button
            onClick={onDelete}
            title="Delete connection"
            style={{
              width: 18, height: 18, borderRadius: "50%",
              background: "#ef4444", color: "#fff",
              border: "2px solid #fff", cursor: "pointer",
              fontSize: 12, fontWeight: 900,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,.3)",
              lineHeight: 1, padding: 0,
            }}
          >×</button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const edgeTypes = { deletable: DeletableEdge };

// ─── Node Config Panels ───────────────────────────────────────

function DelayConfig({ data, onChange }) {
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span style={label}>Wait duration</span>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="number" min="1" value={data.amount || 1}
            onChange={e => onChange({ ...data, amount: parseInt(e.target.value) || 1 })}
            style={{ ...inp, width: 80 }} />
          <select value={data.unit || "hours"}
            onChange={e => onChange({ ...data, unit: e.target.value })}
            style={{ ...inp, flex: 1 }}>
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
            <option value="days">Days</option>
            <option value="weeks">Weeks</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function StartConfig({ data, onChange, templates, audienceLists, onUpdateAudience, workflowId }) {
  const isEvent = (data.triggerType || "hubspot_list") === "message_event";
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (isEvent) return;
    setListsLoading(true);
    wfFetch("/hubspot/lists").then(d => { setLists(d.lists || []); setListsLoading(false); });
  }, [isEvent]);

  const selected = new Set((audienceLists || []).map(l => l.listId));
  const total = (audienceLists || []).reduce((s, l) => s + (l.contactCount || 0), 0);

  const toggle = (list) => {
    const isSelected = selected.has(list.listId);
    const newLists = isSelected
      ? (audienceLists || []).filter(l => l.listId !== list.listId)
      : [...(audienceLists || []), { listId: list.listId, listName: list.listName, contactCount: list.contactCount, listType: list.listType || "STATIC" }];
    onUpdateAudience(newLists);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!audienceLists?.length) return;
    setPreviewLoading(true);
    const d = await wfFetch(`/workflows/${workflowId}/preview-contacts`, {
      method: "POST", body: JSON.stringify({ audienceLists }),
    });
    setPreview(d);
    setPreviewLoading(false);
  };

  const filtered = lists.filter(l => !search || l.listName?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span style={label}>Enrollment Trigger</span>
        <select style={inp} value={data.triggerType || "hubspot_list"}
          onChange={e => onChange({ ...data, triggerType: e.target.value })}>
          <option value="hubspot_list">HubSpot List — enroll on activation</option>
          <option value="message_event">Message Event — enroll when event occurs</option>
        </select>
      </div>

      {isEvent ? (
        <>
          <div style={{ marginBottom: 14 }}>
            <span style={label}>Trigger Event</span>
            <select style={inp} value={data.triggerEvent || "delivered"}
              onChange={e => onChange({ ...data, triggerEvent: e.target.value })}>
              <option value="sent">Message Sent</option>
              <option value="delivered">Message Delivered</option>
              <option value="read">Message Read</option>
              <option value="clicked">Link Clicked</option>
              <option value="replied">WhatsApp Replied</option>
              <option value="opt_out">OPT Out</option>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <span style={label}>Filter by Template (optional)</span>
            <select style={inp} value={data.triggerTemplate || ""}
              onChange={e => onChange({ ...data, triggerTemplate: e.target.value })}>
              <option value="">— Any template (all) —</option>
              {templates.filter(t => t.status === "APPROVED").map(t => (
                <option key={t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              Leave blank to trigger on any template message.
            </div>
          </div>
          <div style={{ padding: 10, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, fontSize: 11, color: "#92400e" }}>
            Contacts are automatically enrolled when the selected WhatsApp event occurs — no manual activation needed.
          </div>
        </>
      ) : (
        <div>
          <span style={label}>Audience Lists</span>
          <input style={{ ...inp, marginBottom: 8 }} placeholder="Search lists…" value={search}
            onChange={e => setSearch(e.target.value)} />

          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", marginBottom: 10 }}>
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {listsLoading ? (
                <div style={{ padding: "16px 12px", fontSize: 12, color: "#94a3b8" }}>Loading lists…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: "16px 12px", fontSize: 12, color: "#94a3b8" }}>No lists found</div>
              ) : filtered.slice(0, 100).map(list => (
                <div key={list.listId} onClick={() => toggle(list)}
                  style={{
                    padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                    background: selected.has(list.listId) ? "#eef2ff" : "#fff",
                    display: "flex", alignItems: "center", gap: 8,
                  }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0, border: `2px solid ${selected.has(list.listId) ? "#6366f1" : "#cbd5e1"}`,
                    background: selected.has(list.listId) ? "#6366f1" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {selected.has(list.listId) && <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>✓</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: selected.has(list.listId) ? "#6366f1" : "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {list.listName}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8", display: "flex", gap: 5, alignItems: "center" }}>
                      <span>{(list.contactCount || 0).toLocaleString()} contacts</span>
                      <span style={{
                        background: list.listType === "ACTIVE" ? "#d1fae5" : "#f1f5f9",
                        color: list.listType === "ACTIVE" ? "#065f46" : "#64748b",
                        padding: "0 4px", borderRadius: 3, fontWeight: 700, fontSize: 9,
                      }}>{list.listType === "ACTIVE" ? "DYNAMIC" : "STATIC"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(audienceLists || []).length > 0 ? (
            <div style={{ padding: 10, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#166534", marginBottom: 6 }}>
                {audienceLists.length} list(s) · {total.toLocaleString()} contacts
              </div>
              {audienceLists.some(l => l.listType === "ACTIVE") && (
                <div style={{ fontSize: 10, color: "#065f46", marginBottom: 8 }}>↻ Dynamic lists auto-sync every 2 min</div>
              )}
              <button onClick={handlePreview} disabled={previewLoading}
                style={{ ...btn("#6366f1", { width: "100%", fontSize: 11, padding: "6px 10px" }), opacity: previewLoading ? .7 : 1 }}>
                {previewLoading ? "Checking…" : "Preview Valid Contacts"}
              </button>
              {preview && (
                <div style={{ marginTop: 8, fontSize: 11 }}>
                  <div style={{ color: "#10b981" }}>✓ {preview.validCount} valid</div>
                  {preview.invalidCount > 0 && <div style={{ color: "#ef4444" }}>✕ {preview.invalidCount} invalid/skipped</div>}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 10, background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, fontSize: 11, color: "#92400e" }}>
              Select at least one list. Contacts are enrolled when you activate the workflow.
            </div>
          )}
        </div>
      )}

      {/* Re-enrollment toggle — applies to both trigger types */}
      <div style={{ marginTop: 16, padding: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}
          onClick={() => onChange({ ...data, allowReenroll: !data.allowReenroll })}>
          <div style={{ flexShrink: 0, marginTop: 2, width: 36, height: 20, borderRadius: 10,
            background: data.allowReenroll ? "#6366f1" : "#cbd5e1", position: "relative", transition: "background .2s" }}>
            <div style={{ position: "absolute", top: 2, left: data.allowReenroll ? 18 : 2, width: 16, height: 16,
              borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Allow Re-enrollment</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              {data.allowReenroll
                ? "Contacts who completed this workflow can be enrolled again."
                : "Each contact is enrolled only once (default). Contacts who already completed are skipped."}
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}

function MessageConfig({ data, onChange, templates }) {
  const bodyComp = t => (t?.components || []).find(c => c.type === "BODY");
  const getVarNums = t => {
    const text = bodyComp(t)?.text || "";
    const matches = text.match(/{{\d+}}/g) || [];
    return [...new Set(matches.map(m => m.replace(/[{}]/g, "")))].sort((a, b) => parseInt(a) - parseInt(b));
  };
  const varNums = data.template ? getVarNums(data.template) : [];
  const mapping = data.variableMapping || {};

  // Detect URL buttons with {{1}} for link tracking
  const urlTrackingButtons = (data.template?.components?.find(c => c.type === "BUTTONS")?.buttons || [])
    .map((btn, idx) => ({ ...btn, buttonIndex: idx }))
    .filter(btn => btn.type === "URL" && btn.url?.includes("{{1}}"));
  const urlButtonConfigs = data.urlButtonConfigs || [];
  const getTargetUrl = (buttonIndex) =>
    (urlButtonConfigs.find(c => c.index === buttonIndex) || {}).value || "";
  const setTargetUrl = (buttonIndex, value) => {
    const existing = urlButtonConfigs.filter(c => c.index !== buttonIndex);
    const updated = value.trim()
      ? [...existing, { index: buttonIndex, mode: "tracking", value: value.trim() }]
      : existing;
    onChange({ ...data, urlButtonConfigs: updated.length ? updated : undefined });
  };
  const HS_PROPS = ["firstname", "lastname", "email", "phone", "company", "jobtitle", "city", "country", "lifecyclestage", "hs_lead_status"];

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span style={label}>WhatsApp Template</span>
        <select value={data.template?.name || ""} onChange={e => {
          const t = templates.find(x => x.name === e.target.value);
          onChange({ ...data, template: t || null, variableMapping: {} });
        }} style={inp}>
          <option value="">— Select a template —</option>
          {templates.filter(t => t.status === "APPROVED").map(t => (
            <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
          ))}
        </select>
      </div>

      {data.template && (
        <div style={{ marginBottom: 14, padding: 10, background: "#f8fafc", borderRadius: 8, fontSize: 12, color: "#475569" }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: "#0f172a" }}>Preview:</div>
          {bodyComp(data.template)?.text || data.template.name}
        </div>
      )}

      {varNums.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <span style={label}>Variable Mapping</span>
          {varNums.map(num => (
            <div key={num} style={{ marginBottom: 8 }}>
              <span style={{ ...label, color: "#6366f1", marginBottom: 3 }}>{`{{${num}}}`}</span>
              <select value={mapping[num] || ""} onChange={e => onChange({ ...data, variableMapping: { ...mapping, [num]: e.target.value } })} style={inp}>
                <option value="">— Select mapping —</option>
                <optgroup label="Contact Properties">
                  {HS_PROPS.map(p => <option key={p} value={`@contact.${p}`}>{p}</option>)}
                </optgroup>
                <option value="@today">Today&apos;s Date</option>
                <option value="@static:">Static text (type below)</option>
              </select>
              {(mapping[num] || "").startsWith("@static:") && (
                <input style={{ ...inp, marginTop: 4 }}
                  placeholder="Enter static value"
                  value={(mapping[num] || "").slice(8)}
                  onChange={e => onChange({ ...data, variableMapping: { ...mapping, [num]: `@static:${e.target.value}` } })}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <span style={label}>Header Media URL (optional)</span>
        <input style={inp} placeholder="https://example.com/image.jpg" value={data.headerMediaUrl || ""}
          onChange={e => onChange({ ...data, headerMediaUrl: e.target.value })} />
      </div>

      {urlTrackingButtons.length > 0 && (
        <div style={{ marginBottom: 14, padding: 10, background: "#f5f3ff", borderRadius: 8, border: "1px solid #ddd6fe" }}>
          <span style={{ ...label, color: "#7c3aed", marginBottom: 8 }}>Link Tracking</span>
          <p style={{ fontSize: 11, color: "#6d28d9", margin: "0 0 10px" }}>
            Enter a destination URL for each tracked button. Each contact gets a unique link — you can see exactly who clicked.
          </p>
          {urlTrackingButtons.map(btn => (
            <div key={btn.buttonIndex} style={{ marginBottom: 8 }}>
              <span style={{ ...label, color: "#7c3aed", marginBottom: 3 }}>"{btn.text}" button destination</span>
              <input
                style={inp}
                placeholder="https://your-destination.com/page"
                value={getTargetUrl(btn.buttonIndex)}
                onChange={e => setTargetUrl(btn.buttonIndex, e.target.value)}
              />
              <span style={{ fontSize: 10, color: "#7c3aed", marginTop: 2, display: "block" }}>
                Base: {btn.url?.replace("{{1}}", "")} + unique token per contact
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 0 }}>
        <div style={{ flex: 1 }}>
          <span style={label}>Max Retries</span>
          <input type="number" min="0" max="5" style={inp} value={data.maxRetries || 0}
            onChange={e => onChange({ ...data, maxRetries: parseInt(e.target.value) || 0 })} />
        </div>
        <div style={{ flex: 1 }}>
          <span style={label}>Retry Delay (min)</span>
          <input type="number" min="5" style={inp} value={data.retryDelayMinutes || 30}
            onChange={e => onChange({ ...data, retryDelayMinutes: parseInt(e.target.value) || 30 })} />
        </div>
      </div>
    </div>
  );
}

function ConditionConfig({ data, onChange }) {
  const groups = data.conditionGroups || [];

  const WA_PROPS = [
    { value: "messageSent",      label: "Message Sent" },
    { value: "messageDelivered", label: "Message Delivered" },
    { value: "messageRead",      label: "Message Read" },
    { value: "messageReplied",   label: "Message Replied" },
    { value: "messageClicked",   label: "Link Clicked" },
    { value: "messageFailed",    label: "Message Failed" },
    { value: "totalSent",        label: "Total Sent (count)" },
    { value: "totalDelivered",   label: "Total Delivered (count)" },
    { value: "totalRead",        label: "Total Read (count)" },
    { value: "totalReplies",     label: "Total Replies (count)" },
    { value: "totalClicks",      label: "Total Clicks (count)" },
    { value: "optedOut",         label: "Opted Out" },
  ];
  const BOOL_OPS  = ["is_true","is_false","is_known","is_unknown"];
  const COUNT_OPS = ["equals","not_equals","greater_than","less_than"];
  const TEXT_OPS  = ["equals","not_equals","contains","not_contains","starts_with","ends_with","is_known","is_unknown"];
  const getOps = (cond) => {
    if (cond.type === "wa_engagement") return cond.property?.startsWith("total") ? COUNT_OPS : BOOL_OPS;
    return TEXT_OPS;
  };

  const addGroup = () => {
    const id = "g" + Date.now().toString(36);
    onChange({ ...data, conditionGroups: [...groups, { id, label: `Group ${groups.length + 1}`, conditions: [{ type: "wa_engagement", property: "messageRead", operator: "is_true", value: "" }], logic: "AND" }] });
  };
  const removeGroup = (gi) => onChange({ ...data, conditionGroups: groups.filter((_, i) => i !== gi) });
  const updateGroup = (gi, patch) => onChange({ ...data, conditionGroups: groups.map((g, i) => i === gi ? { ...g, ...patch } : g) });
  const addCondition = (gi) => {
    const g = groups[gi];
    updateGroup(gi, { conditions: [...(g.conditions||[]), { type: "wa_engagement", property: "messageRead", operator: "is_true", value: "" }] });
  };
  const removeCondition = (gi, ci) => updateGroup(gi, { conditions: (groups[gi].conditions||[]).filter((_,i) => i !== ci) });
  const updateCondition = (gi, ci, patch) => updateGroup(gi, { conditions: (groups[gi].conditions||[]).map((c,i) => i === ci ? { ...c, ...patch } : c) });

  const hasEngagement = groups.some(g => (g.conditions||[]).some(c => c.type === "wa_engagement"));

  return (
    <div>
      {groups.map((group, gi) => {
        const gc = GROUP_COLORS[gi % GROUP_COLORS.length];
        return (
          <div key={group.id} style={{ border: `2px solid ${gc}44`, borderRadius: 10, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ background: `${gc}18`, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: gc, flexShrink: 0 }} />
              <input style={{ ...inp, flex: 1, fontWeight: 700, fontSize: 12, background: "transparent", border: "none", padding: "2px 4px", color: gc }}
                value={group.label || ""} onChange={e => updateGroup(gi, { label: e.target.value })} placeholder={`Group ${gi + 1}`} />
              <select style={{ ...inp, width: 100, fontSize: 11 }} value={group.logic || "AND"} onChange={e => updateGroup(gi, { logic: e.target.value })}>
                <option value="AND">ALL (AND)</option>
                <option value="OR">ANY (OR)</option>
              </select>
              <button onClick={() => removeGroup(gi)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 16, padding: "0 2px" }}>✕</button>
            </div>
            <div style={{ padding: "10px 10px 8px" }}>
              {(group.conditions || []).map((cond, ci) => (
                <div key={ci} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Condition {ci + 1}</span>
                    <button onClick={() => removeCondition(gi, ci)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: 13 }}>✕</button>
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <span style={label}>Type</span>
                    <select style={inp} value={cond.type || "wa_engagement"}
                      onChange={e => updateCondition(gi, ci, { type: e.target.value, property: "", operator: "is_true", value: "" })}>
                      <option value="wa_engagement">WhatsApp Engagement</option>
                      <option value="hubspot_property">HubSpot Property</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <span style={label}>Property</span>
                    {cond.type === "wa_engagement" ? (
                      <select style={inp} value={cond.property || ""}
                        onChange={e => updateCondition(gi, ci, { property: e.target.value, operator: "is_true", value: "" })}>
                        {WA_PROPS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    ) : (
                      <input style={inp} placeholder="HubSpot property name" value={cond.property || ""}
                        onChange={e => updateCondition(gi, ci, { property: e.target.value })} />
                    )}
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <span style={label}>Operator</span>
                    <select style={inp} value={cond.operator || "is_true"} onChange={e => updateCondition(gi, ci, { operator: e.target.value })}>
                      {getOps(cond).map(op => <option key={op} value={op}>{op.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                  {!["is_true","is_false","is_known","is_unknown"].includes(cond.operator) && (
                    <div>
                      <span style={label}>Value</span>
                      <input style={inp} placeholder="Enter value" value={cond.value || ""}
                        onChange={e => updateCondition(gi, ci, { value: e.target.value })} />
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => addCondition(gi)}
                style={{ ...btn(gc, { width: "100%", fontSize: 11, padding: "6px 10px" }), opacity: 0.85 }}>
                + Add Condition
              </button>
            </div>
          </div>
        );
      })}

      <button onClick={addGroup} style={{ ...btn("#8b5cf6", { width: "100%", marginBottom: 12 }) }}>
        + Add Condition Group
      </button>

      <div style={{ padding: "10px 12px", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: 8, fontSize: 12, color: "#64748b", marginBottom: hasEngagement ? 14 : 0 }}>
        <strong>Default</strong> path is taken when no group matches — connect the grey "Default" handle on the canvas.
      </div>

      {hasEngagement && (
        <div>
          <span style={label}>Max Wait for Engagement (minutes, 0 = check immediately)</span>
          <input type="number" min="0" style={inp} value={data.maxWaitMinutes || 0}
            onChange={e => onChange({ ...data, maxWaitMinutes: parseInt(e.target.value) || 0 })} />
        </div>
      )}
    </div>
  );
}

function BatchConfig({ data, onChange }) {
  const batchCount = Math.max(2, Math.min(10, parseInt(data.batchCount) || 2));
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span style={label}>Number of Batches</span>
        <input type="number" min="2" max="10" value={batchCount}
          onChange={e => onChange({ ...data, batchCount: Math.max(2, Math.min(10, parseInt(e.target.value) || 2)) })}
          style={{ ...inp, width: 100 }} />
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
          Min 2 · Max 10
        </div>
      </div>
      <div style={{ padding: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, fontSize: 12, color: "#92400e", lineHeight: 1.6 }}>
        <strong>How it works:</strong> contacts are distributed round-robin across {batchCount} batches.
        10,000 contacts ÷ {batchCount} = ~{Math.floor(10000 / batchCount).toLocaleString()} per batch.
        Connect each numbered handle below the node to its own flow — same or different actions per batch.
      </div>
    </div>
  );
}

function ActionConfig({ data, onChange }) {
  const [pipelines, setPipelines] = useState([]);
  const [owners, setOwners] = useState([]);
  const [hsLoading, setHsLoading] = useState(false);
  const [contactProps, setContactProps] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [hsLists, setHsLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listSearch, setListSearch] = useState("");

  const isListAction = data.actionType === "hs_add_to_list" || data.actionType === "hs_remove_from_list";
  const isHsAction = data.actionType === "hs_create_lead" || data.actionType === "hs_create_deal";
  const objType = data.actionType === "hs_create_deal" ? "deals" : "leads";

  useEffect(() => {
    if (!isHsAction) return;
    setHsLoading(true);
    Promise.all([
      wfFetch(`/hubspot/pipelines/${objType}`),
      wfFetch("/hubspot/owners"),
    ]).then(([pData, oData]) => {
      setPipelines(pData.pipelines || []);
      setOwners(oData.owners || []);
      setHsLoading(false);
    }).catch(() => setHsLoading(false));
  }, [data.actionType]);

  useEffect(() => {
    if (data.actionType !== "update_property") return;
    if (contactProps.length > 0) return;
    setPropsLoading(true);
    wfFetch("/hubspot/properties/contacts").then(d => {
      setContactProps(d.properties || []);
      setPropsLoading(false);
    }).catch(() => setPropsLoading(false));
  }, [data.actionType]);

  useEffect(() => {
    if (!isListAction) return;
    if (hsLists.length > 0) return;
    setListsLoading(true);
    wfFetch("/hubspot/lists").then(d => {
      setHsLists(d.lists || []);
      setListsLoading(false);
    }).catch(() => setListsLoading(false));
  }, [data.actionType]);

  const selectedPipeline = pipelines.find(p =>
    p.id === (data.actionType === "hs_create_deal" ? data.dealPipeline : data.leadPipeline)
  );
  const stages = selectedPipeline?.stages || [];

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <span style={label}>Action Type</span>
        <select style={inp} value={data.actionType || "end_workflow"}
          onChange={e => onChange({ ...data, actionType: e.target.value })}>
          <option value="end_workflow">End Workflow for Contact</option>
          <optgroup label="HubSpot CRM">
            <option value="update_property">Update HubSpot Contact Property</option>
            <option value="hs_create_lead">Create Lead (Lead Object)</option>
            <option value="hs_create_deal">Create Deal (Deal Object)</option>
            <option value="hs_add_to_list">Add Contact to HubSpot List</option>
            <option value="hs_remove_from_list">Remove Contact from HubSpot List</option>
          </optgroup>
          <option value="add_tag">Add Tag</option>
          <option value="remove_tag">Remove Tag</option>
        </select>
      </div>

      {data.actionType === "update_property" && (
        <div style={{ background: "#f0f9ff", borderRadius: 10, padding: "12px", border: "1px solid #bae6fd" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0284c7", marginBottom: 10 }}>Update HubSpot Contact Property</div>
          <div style={{ marginBottom: 10 }}>
            <span style={label}>Property</span>
            {propsLoading ? (
              <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading HubSpot properties…</div>
            ) : (
              <select style={inp} value={data.property || ""}
                onChange={e => onChange({ ...data, property: e.target.value })}>
                <option value="">— Select contact property —</option>
                {contactProps.map(p => (
                  <option key={p.name} value={p.name}>{p.label} ({p.name})</option>
                ))}
              </select>
            )}
            {contactProps.length === 0 && !propsLoading && (
              <input style={{ ...inp, marginTop: 4 }} placeholder="e.g. lifecyclestage" value={data.property || ""}
                onChange={e => onChange({ ...data, property: e.target.value })} />
            )}
          </div>
          <div>
            <span style={label}>New Value</span>
            <input style={inp} placeholder="e.g. customer" value={data.value || ""}
              onChange={e => onChange({ ...data, value: e.target.value })} />
          </div>
        </div>
      )}

      {(data.actionType === "add_tag" || data.actionType === "remove_tag") && (
        <div>
          <span style={label}>Tag Name</span>
          <input style={inp} placeholder="e.g. hot-lead" value={data.value || ""}
            onChange={e => onChange({ ...data, value: e.target.value })} />
        </div>
      )}

      {isListAction && (
        <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "12px", border: "1px solid #86efac" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 10 }}>
            {data.actionType === "hs_add_to_list" ? "Add Contact to HubSpot List" : "Remove Contact from HubSpot List"}
          </div>
          {listsLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading HubSpot lists…</div>
          ) : (
            <div>
              <span style={label}>HubSpot List</span>
              <input
                style={{ ...inp, marginBottom: 6 }}
                placeholder="Search lists…"
                value={listSearch}
                onChange={e => setListSearch(e.target.value)}
              />
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", maxHeight: 200, overflowY: "auto" }}>
                {hsLists
                  .filter(l => !listSearch || l.listName?.toLowerCase().includes(listSearch.toLowerCase()))
                  .map(l => (
                    <div
                      key={l.listId}
                      onClick={() => { onChange({ ...data, listId: l.listId, listName: l.listName }); setListSearch(""); }}
                      style={{
                        padding: "8px 10px", cursor: "pointer", fontSize: 12,
                        background: data.listId === l.listId ? "#f0fdf4" : "#fff",
                        borderBottom: "1px solid #f1f5f9",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        color: data.listId === l.listId ? "#15803d" : "#0f172a",
                        fontWeight: data.listId === l.listId ? 700 : 400,
                      }}
                      onMouseEnter={e => { if (data.listId !== l.listId) e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={e => { if (data.listId !== l.listId) e.currentTarget.style.background = "#fff"; }}
                    >
                      <span>{l.listName}</span>
                      <span style={{ fontSize: 10, color: l.listType === "ACTIVE" ? "#15803d" : "#94a3b8", fontWeight: 700 }}>
                        {l.listType === "ACTIVE" ? "Dynamic" : "Static"}
                        {data.listId === l.listId && " ✓"}
                      </span>
                    </div>
                  ))
                }
                {hsLists.filter(l => !listSearch || l.listName?.toLowerCase().includes(listSearch.toLowerCase())).length === 0 && (
                  <div style={{ padding: 10, color: "#94a3b8", fontSize: 12 }}>No lists match</div>
                )}
              </div>
              {data.listId && (
                <div style={{ fontSize: 11, color: "#15803d", marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>✓ {data.listName || data.listId}</span>
                  <button onClick={() => onChange({ ...data, listId: "", listName: "" })} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 12 }}>✕ Clear</button>
                </div>
              )}
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
                Works for all enrollments — HubSpot contact will be auto-resolved by phone number if needed.
              </div>
            </div>
          )}
        </div>
      )}

      {data.actionType === "hs_create_lead" && (
        <div style={{ background: "#f0f9ff", borderRadius: 10, padding: "12px", border: "1px solid #bae6fd" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0284c7", marginBottom: 10 }}>HubSpot Lead Configuration</div>
          {hsLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading HubSpot data…</div>
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={label}>Lead Pipeline</span>
                <select style={inp} value={data.leadPipeline || ""}
                  onChange={e => onChange({ ...data, leadPipeline: e.target.value, leadStage: "" })}>
                  <option value="">— Select Pipeline —</option>
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={label}>Lead Stage</span>
                <select style={inp} value={data.leadStage || ""}
                  onChange={e => onChange({ ...data, leadStage: e.target.value })}>
                  <option value="">— Select Stage —</option>
                  {stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <span style={label}>Lead Owner</span>
                <select style={inp} value={data.leadOwner || ""}
                  onChange={e => onChange({ ...data, leadOwner: e.target.value })}>
                  <option value="">— Unassigned —</option>
                  {owners.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      )}

      {data.actionType === "hs_create_deal" && (
        <div style={{ background: "#f0fdf4", borderRadius: 10, padding: "12px", border: "1px solid #86efac" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 10 }}>HubSpot Deal Configuration</div>
          {hsLoading ? (
            <div style={{ color: "#94a3b8", fontSize: 12 }}>Loading HubSpot data…</div>
          ) : (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={label}>Deal Pipeline</span>
                <select style={inp} value={data.dealPipeline || ""}
                  onChange={e => onChange({ ...data, dealPipeline: e.target.value, dealStage: "" })}>
                  <option value="">— Select Pipeline —</option>
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={label}>Deal Stage</span>
                <select style={inp} value={data.dealStage || ""}
                  onChange={e => onChange({ ...data, dealStage: e.target.value })}>
                  <option value="">— Select Stage —</option>
                  {stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <span style={label}>Deal Owner</span>
                <select style={inp} value={data.dealOwner || ""}
                  onChange={e => onChange({ ...data, dealOwner: e.target.value })}>
                  <option value="">— Unassigned —</option>
                  {owners.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NodeConfigPanel({ node, onUpdate, onDelete, templates, onClose, audienceLists, onUpdateAudience, workflowId }) {
  if (!node) return null;
  const { type, data } = node;
  const meta = NODE_META[type] || {};

  const handleChange = (newData) => {
    onUpdate(node.id, newData);
  };

  return (
    <div style={{ width: 320, background: "#fff", borderLeft: "1px solid #e2e8f0", display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: meta.color, color: "#fff" }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{meta.icon} Configure: {meta.label}</div>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,.2)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "4px 8px", fontSize: 14 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: 16, overflowY: "auto" }}>
        {/* Common: label */}
        {type !== "start" && type !== "end" && (
          <div style={{ marginBottom: 14 }}>
            <span style={label}>Node Label</span>
            <input style={inp} value={data.label || ""} onChange={e => handleChange({ ...data, label: e.target.value })} />
          </div>
        )}

        {type === "delay"     && <DelayConfig     data={data} onChange={handleChange} />}
        {type === "message"   && <MessageConfig   data={data} onChange={handleChange} templates={templates} />}
        {type === "condition" && <ConditionConfig data={data} onChange={handleChange} />}
        {type === "batch"     && <BatchConfig     data={data} onChange={handleChange} />}
        {type === "action"    && <ActionConfig    data={data} onChange={handleChange} />}
        {type === "start"     && <StartConfig     data={data} onChange={handleChange} templates={templates} audienceLists={audienceLists} onUpdateAudience={onUpdateAudience} workflowId={workflowId} />}
        {type === "end"       && <div style={{ color: "#94a3b8", fontSize: 13 }}>This node terminates the workflow for the contact.</div>}
      </div>

      {/* Footer */}
      <div style={{ padding: 12, borderTop: "1px solid #f1f5f9", display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ ...btn("#6366f1", { flex: 1 }) }}>Save Node</button>
        {type !== "start" && (
          <button onClick={() => onDelete(node.id)} style={{ ...btn("#ef4444", { padding: "8px 16px", flexShrink: 0 }) }}>Delete</button>
        )}
      </div>
    </div>
  );
}

// ─── Node Palette ─────────────────────────────────────────────
function NodePalette() {
  const onDragStart = (e, type) => {
    e.dataTransfer.setData("application/reactflow", type);
    e.dataTransfer.effectAllowed = "move";
  };

  return (
    <div style={{ width: 180, background: "#fff", borderRight: "1px solid #e2e8f0", padding: 12, overflowY: "auto" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 1, marginBottom: 12, textTransform: "uppercase" }}>Add Nodes</div>
      {PALETTE_NODES.map(type => {
        const m = NODE_META[type];
        return (
          <div key={type} draggable
            onDragStart={e => onDragStart(e, type)}
            style={{
              background: "#f8fafc", border: `2px solid ${m.color}33`, borderRadius: 10,
              padding: "10px 12px", marginBottom: 10, cursor: "grab", userSelect: "none",
              display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "#0f172a",
              transition: "box-shadow .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 0 3px ${m.color}33`}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
          >
            <span style={{ fontSize: 16 }}>{m.icon}</span>
            <span>{m.label}</span>
          </div>
        );
      })}
      <div style={{ marginTop: 16, padding: 10, background: "#f8fafc", borderRadius: 8, fontSize: 11, color: "#94a3b8", lineHeight: 1.6 }}>
        Drag nodes onto the canvas and connect them by dragging from one handle to another.
      </div>
    </div>
  );
}

// ─── Audience Sidebar ─────────────────────────────────────────
function AudienceSidebar({ audienceLists, onUpdate, workflowId }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setLoading(true);
    wfFetch("/hubspot/lists").then(d => { setLists(d.lists || []); setLoading(false); });
  }, []);

  const selected = new Set((audienceLists || []).map(l => l.listId));

  const toggle = (list) => {
    const isSelected = selected.has(list.listId);
    const newLists = isSelected
      ? (audienceLists || []).filter(l => l.listId !== list.listId)
      : [...(audienceLists || []), { listId: list.listId, listName: list.listName, contactCount: list.contactCount, listType: list.listType || "STATIC" }];
    onUpdate(newLists);
  };

  const handlePreview = async () => {
    if (!audienceLists?.length) return;
    setPreviewLoading(true);
    const d = await wfFetch(`/workflows/${workflowId}/preview-contacts`, {
      method: "POST", body: JSON.stringify({ audienceLists }),
    });
    setPreview(d);
    setPreviewLoading(false);
  };

  const filtered = lists.filter(l => !search || l.listName?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ width: 260, background: "#fff", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "12px 14px", borderBottom: "1px solid #e2e8f0", fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
        📋 Audience
      </div>
      <div style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>
        <input style={inp} placeholder="Search lists…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 8px" }}>
        {loading ? <div style={{ color: "#94a3b8", fontSize: 12, padding: 10 }}>Loading…</div> :
          filtered.slice(0, 100).map(list => (
            <div key={list.listId}
              onClick={() => toggle(list)}
              style={{
                padding: "8px 10px", borderRadius: 8, marginBottom: 4, cursor: "pointer",
                background: selected.has(list.listId) ? "#eef2ff" : "#f8fafc",
                border: `1px solid ${selected.has(list.listId) ? "#6366f1" : "#e2e8f0"}`,
              }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: selected.has(list.listId) ? "#6366f1" : "#0f172a" }}>{list.listName}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", display: "flex", alignItems: "center", gap: 5 }}>
                <span>{list.contactCount?.toLocaleString() || 0} contacts</span>
                <span style={{
                  background: list.listType === "ACTIVE" ? "#d1fae5" : "#f1f5f9",
                  color: list.listType === "ACTIVE" ? "#065f46" : "#64748b",
                  padding: "1px 5px", borderRadius: 4, fontWeight: 700, fontSize: 10,
                }}>{list.listType === "ACTIVE" ? "DYNAMIC" : "STATIC"}</span>
              </div>
            </div>
          ))
        }
      </div>
      {audienceLists?.length > 0 && (
        <div style={{ padding: 10, borderTop: "1px solid #f1f5f9" }}>
          <div style={{ fontSize: 11, color: "#475569", marginBottom: 8, fontWeight: 600 }}>
            {audienceLists.length} list(s) selected
          </div>
          {audienceLists.some(l => l.listType === "ACTIVE") && (
            <div style={{ fontSize: 10, color: "#065f46", background: "#d1fae5", border: "1px solid #6ee7b7", padding: "4px 8px", borderRadius: 6, marginBottom: 8 }}>
              ↻ Dynamic lists auto-sync every 2 min
            </div>
          )}
          <button onClick={handlePreview} disabled={previewLoading}
            style={{ ...btn("#6366f1", { width: "100%", fontSize: 12 }), opacity: previewLoading ? .7 : 1 }}>
            {previewLoading ? "Checking…" : "Preview Contacts"}
          </button>
          {preview && (
            <div style={{ marginTop: 8, fontSize: 11 }}>
              <div style={{ color: "#10b981" }}>✓ {preview.validCount} valid</div>
              {preview.invalidCount > 0 && <div style={{ color: "#ef4444" }}>✕ {preview.invalidCount} invalid/skipped</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Test Workflow Modal ───────────────────────────────────────
function TestWorkflowModal({ workflow, onClose }) {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleTest = async () => {
    if (!phone.trim()) { setError("Enter a test phone number"); return; }
    setLoading(true); setError(""); setResult(null);
    const d = await wfFetch(`/workflows/${workflow.id}/test`, {
      method: "POST",
      body: JSON.stringify({ phone: phone.trim() }),
    });
    setLoading(false);
    if (d.success) setResult(d);
    else setError(d.error || "Simulation failed");
  };

  const typeIcon  = { start: "▶", delay: "⏱", message: "💬", condition: "⚡", batch: "⊞", action: "⚙", end: "⏹", error: "⚠" };
  const typeColor = { start: "#10b981", delay: "#f59e0b", message: "#6366f1", condition: "#8b5cf6", batch: "#f97316", action: "#06b6d4", end: "#64748b", error: "#ef4444" };
  const statusStyle = {
    completed: { bg: "#f0fdf4", color: "#10b981" },
    simulated: { bg: "#eff6ff", color: "#3b82f6" },
    terminal:  { bg: "#f8fafc", color: "#64748b" },
    error:     { bg: "#fef2f2", color: "#ef4444" },
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, width: 580, maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,.2)" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Test Workflow</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{workflow.name} — dry run, no real messages sent</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#94a3b8" }}>✕</button>
        </div>

        {/* Input */}
        <div style={{ padding: "14px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <span style={label}>Test Phone Number</span>
          <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            <input style={{ ...inp, flex: 1 }} placeholder="e.g. 919876543210 (with country code, no +)"
              value={phone} onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleTest()} />
            <button onClick={handleTest} disabled={loading}
              style={{ ...btn("#6366f1"), flexShrink: 0, opacity: loading ? .7 : 1 }}>
              {loading ? "Running…" : "▶ Simulate"}
            </button>
          </div>
          {error && <div style={{ color: "#ef4444", fontSize: 12 }}>{error}</div>}
          <div style={{ fontSize: 11, color: "#94a3b8" }}>Conditions always take the YES branch in test mode. Delays are skipped.</div>
        </div>

        {/* Steps */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
          {result && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 16 }}>
                Simulation for {result.phone} — {result.steps.length} step(s)
              </div>
              {result.steps.map((step, i) => {
                const c = typeColor[step.nodeType] || "#94a3b8";
                const ss = statusStyle[step.status] || { bg: "#f8fafc", color: "#94a3b8" };
                return (
                  <div key={i} style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div style={{ width: 34, height: 34, borderRadius: "50%", background: c + "18", border: `2.5px solid ${c}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>
                        {typeIcon[step.nodeType] || "●"}
                      </div>
                      {i < result.steps.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 12, background: "#e2e8f0", marginTop: 4 }} />}
                    </div>
                    <div style={{ flex: 1, paddingTop: 4 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{step.label}</div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: ss.color, background: ss.bg, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>
                          {step.status}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#475569", background: "#f8fafc", borderRadius: 8, padding: "8px 12px" }}>{step.message}</div>
                      {step.detail && (
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6, padding: "0 4px" }}>
                          {Object.entries(step.detail).filter(([, v]) => v != null && v !== "none" && v !== "").map(([k, v]) => (
                            <span key={k} style={{ marginRight: 12 }}><strong>{k}:</strong> {String(v)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {!result && !loading && (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, paddingTop: 40 }}>
              Enter a phone number above and click Simulate to see how this workflow would execute.
            </div>
          )}
        </div>

        <div style={{ padding: "12px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btn("#f1f5f9", { color: "#475569" })}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Workflow Builder Canvas ──────────────────────────────────
function WorkflowCanvas({ workflow, onSave, onBack, templates }) {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(workflow.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflow.edges || []);
  const [selectedNode, setSelectedNode] = useState(null);
  const [audienceLists, setAudienceLists] = useState(workflow.audienceLists || []);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [showTest, setShowTest] = useState(false);
  const [cycleError, setCycleError] = useState("");
  const { project } = useReactFlow();

  // Refs so auto-save timer always reads the latest state
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const audienceListsRef = useRef(audienceLists);
  const autoSaveTimer = useRef(null);
  const isFirstRender = useRef(true);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { audienceListsRef.current = audienceLists; }, [audienceLists]);

  const doAutoSave = useCallback(async () => {
    setSaving(true);
    setSaveMsg("Auto-saving…");
    const d = await wfFetch(`/workflows/${workflow.id}`, {
      method: "PUT",
      body: JSON.stringify({ nodes: nodesRef.current, edges: edgesRef.current, audienceLists: audienceListsRef.current }),
    });
    setSaving(false);
    if (d.success) { setSaveMsg("Auto-saved"); setTimeout(() => setSaveMsg(""), 2500); }
    else setSaveMsg("Auto-save failed");
  }, [workflow.id]);

  // Debounced auto-save: fires 2s after the last change
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setSaveMsg("Unsaved changes…");
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(doAutoSave, 2000);
    return () => clearTimeout(autoSaveTimer.current);
  }, [nodes, edges, audienceLists, doAutoSave]);

  // Block keyboard-delete of the start node
  const handleNodesChange = useCallback((changes) => {
    const safe = changes.filter(c => {
      if (c.type !== "remove") return true;
      const n = nodesRef.current.find(nd => nd.id === c.id);
      return n?.type !== "start";
    });
    onNodesChange(safe);
  }, [onNodesChange]);

  // Keep start node audience count in sync
  useEffect(() => {
    const total = audienceLists.reduce((s, l) => s + (l.contactCount || 0), 0);
    setNodes(nds => nds.map(n => n.type === "start" ? { ...n, data: { ...n.data, audienceCount: total || null } } : n));
  }, [audienceLists, setNodes]);

  const onConnect = useCallback((params) => {
    setCycleError("");
    // Check for cycles before allowing the connection
    setNodes(currentNodes => {
      setEdges(currentEdges => {
        if (wouldCreateCycle(currentNodes, currentEdges, params.source, params.target)) {
          setCycleError("Cannot connect: this would create an infinite loop. Nodes cannot be connected in a continuous cycle.");
          setTimeout(() => setCycleError(""), 5000);
          return currentEdges;
        }
        // Determine edge colour and label based on handle
        let edgeColor = "#6366f1";
        let edgeLabel;
        const sh = params.sourceHandle || "";
        if (sh === "yes") { edgeColor = "#10b981"; edgeLabel = "YES"; }
        else if (sh === "no") { edgeColor = "#ef4444"; edgeLabel = "NO"; }
        else if (sh === "default") { edgeColor = "#64748b"; edgeLabel = "Default"; }
        else if (sh.startsWith("group_")) {
          // Find matching group label from the source node
          const srcNode = currentNodes.find(n => n.id === params.source);
          const gId = sh.slice(6);
          const grp = (srcNode?.data?.conditionGroups || []).find(g => g.id === gId);
          const grpIdx = (srcNode?.data?.conditionGroups || []).findIndex(g => g.id === gId);
          edgeColor = GROUP_COLORS[grpIdx >= 0 ? grpIdx % GROUP_COLORS.length : 0];
          edgeLabel = grp?.label || sh;
        } else if (sh.startsWith("batch_")) {
          const batchIdx = parseInt(sh.slice(6));
          edgeColor = BATCH_COLORS[batchIdx % BATCH_COLORS.length];
          edgeLabel = `Batch ${batchIdx + 1}`;
        }
        return addEdge({
          ...params,
          type: "deletable",
          markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: edgeColor },
          style: { strokeWidth: 2, stroke: edgeColor },
          label: edgeLabel,
          labelStyle: { fontSize: 10, fontWeight: 700, fill: edgeColor },
          labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
        }, currentEdges);
      });
      return currentNodes;
    });
  }, [setEdges, setNodes]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("application/reactflow");
    if (!type) return;
    const bounds = reactFlowWrapper.current.getBoundingClientRect();
    const pos = project({ x: e.clientX - bounds.left, y: e.clientY - bounds.top });
    const id = `${type}_${Date.now()}`;
    setNodes(nds => [...nds, { id, type, position: pos, data: getDefaultData(type) }]);
  }, [project, setNodes]);

  const onNodeClick = useCallback((_e, node) => setSelectedNode(node), []);
  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  const updateNodeData = (nodeId, newData) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data: newData } : n));
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: newData } : prev);
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  };

  const handleSave = async () => {
    clearTimeout(autoSaveTimer.current);
    setSaving(true);
    const d = await wfFetch(`/workflows/${workflow.id}`, {
      method: "PUT",
      body: JSON.stringify({ nodes, edges, audienceLists }),
    });
    setSaving(false);
    if (d.success) { setSaveMsg("Saved!"); setTimeout(() => setSaveMsg(""), 2000); }
    else setSaveMsg(d.error || "Save failed");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f1f5f9" }}>
      {/* Top bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, zIndex: 10, flexWrap: "wrap" }}>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13, color: "#475569" }}>
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 120 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{workflow.name}</div>
          <div style={{ fontSize: 12, color: STATUS_COLORS[workflow.status] || "#94a3b8", fontWeight: 600 }}>
            {workflow.status?.toUpperCase()}
          </div>
        </div>
        {cycleError && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 12px", fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
            ⚠ {cycleError}
          </div>
        )}
        <button onClick={() => setShowTest(true)}
          style={{ ...btn("#f59e0b", { fontSize: 13 }) }}>
          ▶ Test
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {saveMsg && (
            <span style={{
              fontSize: 12,
              color: saveMsg === "Saved!" || saveMsg === "Auto-saved" ? "#10b981"
                : saveMsg === "Unsaved changes…" || saveMsg === "Auto-saving…" ? "#f59e0b"
                : "#ef4444",
              fontWeight: 600,
            }}>{saveMsg}</span>
          )}
          <button onClick={handleSave} disabled={saving} style={{ ...btn("#6366f1"), opacity: saving ? .7 : 1 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Builder body */}
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Node palette */}
        <NodePalette />

        {/* Canvas */}
        <div ref={reactFlowWrapper} style={{ flex: 1, height: "100%" }}>
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            deleteKeyCode={["Backspace", "Delete"]}
            fitView
            defaultEdgeOptions={{
              type: "deletable",
              markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color: "#6366f1" },
              style: { strokeWidth: 2, stroke: "#6366f1" },
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#e2e8f0" />
            <Controls style={{ bottom: 16, left: 16 }} />
            <MiniMap nodeStrokeWidth={3} style={{ bottom: 16, right: selectedNode ? 340 : 16, borderRadius: 10, border: "1px solid #e2e8f0" }} />
          </ReactFlow>
        </div>

        {/* Node config panel */}
        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={updateNodeData}
            onDelete={deleteNode}
            templates={templates}
            onClose={() => setSelectedNode(null)}
            audienceLists={audienceLists}
            onUpdateAudience={setAudienceLists}
            workflowId={workflow.id}
          />
        )}
      </div>

      {/* Test modal */}
      {showTest && (
        <TestWorkflowModal workflow={workflow} onClose={() => setShowTest(false)} />
      )}
    </div>
  );
}

// Wrap in ReactFlowProvider
function WorkflowBuilder(props) {
  return (
    <ReactFlowProvider>
      <WorkflowCanvas {...props} />
    </ReactFlowProvider>
  );
}

// ─── Workflow Validation & Activation Modal ───────────────────
function ActivateModal({ workflow, onClose, onActivated }) {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [errors, setErrors] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    wfFetch(`/workflows/${workflow.id}/validate`)
      .then(d => {
        setErrors(d.errors || []);
        setValidating(false);
      })
      .catch(() => {
        setErrors(["Could not reach the server. Please try again."]);
        setValidating(false);
      });
  }, [workflow.id]);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const d = await wfFetch(`/workflows/${workflow.id}/status`, { method: "POST", body: JSON.stringify({ action: "activate" }) });
      if (d.success) {
        setResult({ enrolled: d.enrolled, total: d.total });
        setTimeout(() => { onActivated(d.workflow); onClose(); }, 2000);
      } else {
        setErrors(d.errors || [d.error || "Activation failed"]);
      }
    } catch {
      setErrors(["Network error — could not activate workflow. Please try again."]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 480, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: "#0f172a" }}>
          {result ? "✅ Workflow Activated" : "Activate Workflow"}
        </div>

        {validating && <div style={{ color: "#94a3b8", fontSize: 13 }}>Validating workflow…</div>}

        {!validating && errors.length > 0 && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: "#dc2626", marginBottom: 8, fontSize: 13 }}>Validation errors:</div>
            {errors.map((e, i) => <div key={i} style={{ fontSize: 12, color: "#dc2626", marginBottom: 4 }}>• {e}</div>)}
          </div>
        )}

        {!validating && errors.length === 0 && !result && (
          <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ color: "#166534", fontSize: 13 }}>✓ Workflow is valid and ready to activate. Contacts from all selected HubSpot lists will be enrolled immediately.</div>
          </div>
        )}

        {result && (
          <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div style={{ color: "#166534", fontSize: 13, fontWeight: 600 }}>
              {result.enrolled} contacts enrolled (of {result.total} in selected lists).
            </div>
          </div>
        )}

        {!result && (
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ ...btn("#f1f5f9", { color: "#475569" }) }}>Cancel</button>
            <button onClick={handleActivate} disabled={loading || validating || errors.length > 0}
              style={{ ...btn("#10b981"), opacity: (loading || validating || errors.length > 0) ? .6 : 1 }}>
              {loading ? "Activating…" : "Activate Workflow"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Analytics Dashboard ──────────────────────────────────────
function WorkflowAnalytics({ workflow, onBack }) {
  const [analytics, setAnalytics] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [tab, setTab] = useState("overview");
  const [minRead,    setMinRead]    = useState("");
  const [minClick,   setMinClick]   = useState("");
  const [minReply,   setMinReply]   = useState("");
  const [sortCol,    setSortCol]    = useState("startedAt");
  const [sortDir,    setSortDir]    = useState("desc");

  const load = async () => {
    setLoading(true);
    const [a, e] = await Promise.all([
      wfFetch(`/workflows/${workflow.id}/analytics`),
      wfFetch(`/workflows/${workflow.id}/enrollments`),
    ]);
    setAnalytics(a);
    setEnrollments(e.enrollments || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [workflow.id]);

  const loadTimeline = async (enrollment) => {
    const d = await wfFetch(`/workflows/${workflow.id}/enrollments/${enrollment.id}`);
    setSelectedEnrollment(d.enrollment || enrollment);
  };

  if (loading) return <div style={{ padding: 40, color: "#94a3b8" }}>Loading analytics…</div>;

  const s = analytics?.stats || {};
  const Stat = ({ label: lbl, value, color = "#0f172a" }) => (
    <div style={{ background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,.07)", textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, marginTop: 4 }}>{lbl}</div>
    </div>
  );

  return (
    <div style={{ height: "100%", overflowY: "auto", background: "#f1f5f9" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13, color: "#475569" }}>
          ← Back
        </button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{workflow.name} — Analytics</div>
          <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLORS[workflow.status] }}>{workflow.status?.toUpperCase()}</span>
        </div>
        <button onClick={load} style={{ marginLeft: "auto", ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0" }) }}>Refresh</button>
      </div>

      {/* Tabs */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", padding: "0 24px" }}>
        {["overview", "contacts"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "none", border: "none", borderBottom: `3px solid ${tab === t ? "#6366f1" : "transparent"}`,
            color: tab === t ? "#6366f1" : "#94a3b8", fontWeight: 700, padding: "12px 16px", cursor: "pointer", fontSize: 13,
          }}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      <div style={{ padding: 24 }}>
        {tab === "overview" && (
          <>
            {/* Enrollment stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
              <Stat label="Enrolled" value={s.enrolled || 0} />
              <Stat label="Active" value={s.active || 0} color="#f59e0b" />
              <Stat label="Completed" value={s.completed || 0} color="#10b981" />
              <Stat label="Failed" value={s.failed || 0} color="#ef4444" />
            </div>

            {/* Messaging stats */}
            <div style={{ ...card(), marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#0f172a" }}>Messaging Metrics</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 }}>
                {[
                  { l: "Sent", v: s.sent || 0, c: "#6366f1" },
                  { l: "Delivered", v: s.delivered || 0, c: "#10b981" },
                  { l: "Read", v: s.read || 0, c: "#06b6d4" },
                  { l: "Replied", v: s.replied || 0, c: "#8b5cf6" },
                  { l: "Clicked", v: s.clicks || 0, c: "#f59e0b" },
                  { l: "Failed", v: s.failedMessages || 0, c: "#ef4444" },
                ].map(({ l, v, c }) => (
                  <div key={l} style={{ textAlign: "center", padding: "12px 8px", background: "#f8fafc", borderRadius: 10 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{v}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 16, borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
                {[
                  { l: "Delivery Rate", v: `${s.deliveryRate || 0}%` },
                  { l: "Read Rate", v: `${s.readRate || 0}%` },
                  { l: "Reply Rate", v: `${s.replyRate || 0}%` },
                  { l: "Click Rate", v: `${s.clickRate || 0}%` },
                ].map(({ l, v }) => (
                  <div key={l}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{v}</span>
                    <span style={{ fontSize: 12, color: "#94a3b8", marginLeft: 4 }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Node stats */}
            {analytics?.nodeStats && Object.keys(analytics.nodeStats).length > 0 && (
              <div style={card()}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16, color: "#0f172a" }}>Node Performance</div>
                {workflow.nodes?.filter(n => n.type === "message").map(n => {
                  const ns = analytics.nodeStats[n.id] || {};
                  return (
                    <div key={n.id} style={{ padding: "12px 0", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>{n.data?.label || n.data?.template?.name || "Message"}</div>
                      <div style={{ display: "flex", gap: 20, fontSize: 12 }}>
                        {[["Sent", ns.sent], ["Delivered", ns.delivered], ["Read", ns.read], ["Replied", ns.replied], ["Clicked", ns.clicked], ["Failed", ns.failed]].map(([l, v]) => (
                          <span key={l}><span style={{ fontWeight: 700, color: "#0f172a" }}>{v || 0}</span> <span style={{ color: "#94a3b8" }}>{l}</span></span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "contacts" && (() => {
          const toggleSort = (col) => {
            if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
            else { setSortCol(col); setSortDir("desc"); }
          };

          const filtered = enrollments
            .filter(e => {
              if (minRead  !== "" && (e.waProps?.totalRead    || 0) < parseInt(minRead))  return false;
              if (minClick !== "" && (e.waProps?.totalClicks  || 0) < parseInt(minClick)) return false;
              if (minReply !== "" && (e.waProps?.totalReplies || 0) < parseInt(minReply)) return false;
              return true;
            })
            .sort((a, b) => {
              let av, bv;
              if      (sortCol === "startedAt")    { av = a.startedAt || "";                 bv = b.startedAt || ""; }
              else if (sortCol === "totalRead")    { av = a.waProps?.totalRead    || 0;       bv = b.waProps?.totalRead    || 0; }
              else if (sortCol === "totalClicks")  { av = a.waProps?.totalClicks  || 0;       bv = b.waProps?.totalClicks  || 0; }
              else if (sortCol === "totalReplies") { av = a.waProps?.totalReplies || 0;       bv = b.waProps?.totalReplies || 0; }
              else if (sortCol === "totalSent")    { av = a.waProps?.totalSent    || 0;       bv = b.waProps?.totalSent    || 0; }
              else                                 { av = a[sortCol] || "";                   bv = b[sortCol] || ""; }
              if (av < bv) return sortDir === "asc" ? -1 : 1;
              if (av > bv) return sortDir === "asc" ?  1 : -1;
              return 0;
            });

          const SortTh = ({ col, label, left }) => (
            <th onClick={() => toggleSort(col)} style={{ padding: "10px 14px", textAlign: left ? "left" : "center", fontWeight: 700, color: sortCol === col ? "#6366f1" : "#475569", fontSize: 12, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
              {label} {sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : ""}
            </th>
          );

          return (
            <div style={card({ padding: 0 })}>
              <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", background: "#fafafa" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Filter:</span>
                {[
                  { label: "Read ≥",    val: minRead,  set: setMinRead  },
                  { label: "Clicks ≥",  val: minClick, set: setMinClick },
                  { label: "Replies ≥", val: minReply, set: setMinReply },
                ].map(({ label, val, set }) => (
                  <label key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#64748b" }}>
                    {label}
                    <input type="number" min="0" value={val} onChange={e => set(e.target.value)}
                      style={{ width: 52, padding: "3px 6px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                  </label>
                ))}
                {(minRead !== "" || minClick !== "" || minReply !== "") && (
                  <button onClick={() => { setMinRead(""); setMinClick(""); setMinReply(""); }}
                    style={{ fontSize: 11, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>
                    Clear
                  </button>
                )}
                <span style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}>
                  {filtered.length} of {enrollments.length} contacts
                </span>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <SortTh col="contactName" label="Contact" left />
                      <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: 12 }}>Phone</th>
                      <SortTh col="status"       label="Status"  left />
                      <SortTh col="startedAt"    label="Started" left />
                      <SortTh col="totalSent"    label="Sent" />
                      <SortTh col="totalRead"    label="Read" />
                      <SortTh col="totalReplies" label="Replied" />
                      <SortTh col="totalClicks"  label="Clicked" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(e => (
                      <tr key={e.id} onClick={() => loadTimeline(e)} style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                        onMouseEnter={ev => ev.currentTarget.style.background = "#f8fafc"}
                        onMouseLeave={ev => ev.currentTarget.style.background = ""}>
                        <td style={{ padding: "10px 14px", fontWeight: 600 }}>{e.contactName || "—"}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{e.contactPhone}</td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: (STATUS_COLORS[e.status] || "#94a3b8") + "22", color: STATUS_COLORS[e.status] || "#94a3b8", padding: "2px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11 }}>
                            {e.status}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>
                          {e.startedAt ? new Date(e.startedAt).toLocaleDateString("en-IN") : "—"}
                        </td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#6366f1" }}>{e.waProps?.totalSent    || 0}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#06b6d4" }}>{e.waProps?.totalRead    || 0}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#8b5cf6" }}>{e.waProps?.totalReplies || 0}</td>
                        <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#f59e0b" }}>{e.waProps?.totalClicks  || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                    {enrollments.length === 0 ? "No contacts enrolled yet" : "No contacts match your filters"}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Contact timeline modal */}
      {selectedEnrollment && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: 520, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 40px rgba(0,0,0,.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{selectedEnrollment.contactName || selectedEnrollment.contactPhone}</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{selectedEnrollment.contactPhone} · {selectedEnrollment.status}</div>
              </div>
              <button onClick={() => setSelectedEnrollment(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#475569", marginBottom: 12 }}>Journey Timeline</div>
            <div style={{ position: "relative", paddingLeft: 20 }}>
              {(selectedEnrollment.timeline || []).map((ev, i) => {
                const evColors = { enrolled: "#10b981", message_sent: "#6366f1", status: "#06b6d4", condition: "#8b5cf6", delay: "#f59e0b", error: "#ef4444", complete: "#10b981", reply: "#8b5cf6", clicked: "#f59e0b" };
                const c = evColors[ev.type] || "#94a3b8";
                return (
                  <div key={i} style={{ position: "relative", marginBottom: 16, paddingLeft: 20 }}>
                    <div style={{ position: "absolute", left: -20, top: 2, width: 10, height: 10, borderRadius: "50%", background: c, border: `2px solid ${c}33` }} />
                    {i < (selectedEnrollment.timeline || []).length - 1 && (
                      <div style={{ position: "absolute", left: -16, top: 12, width: 2, height: "calc(100% + 4px)", background: "#e2e8f0" }} />
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: c }}>{ev.action}</div>
                    {ev.detail && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{String(ev.detail).slice(0, 120)}</div>}
                    <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 2 }}>
                      {ev.at ? new Date(ev.at).toLocaleString("en-IN") : ""}
                    </div>
                  </div>
                );
              })}
              {!selectedEnrollment.timeline?.length && <div style={{ color: "#94a3b8", fontSize: 13 }}>No timeline events</div>}
            </div>

            {/* WA props summary */}
            <div style={{ marginTop: 16, padding: 14, background: "#f8fafc", borderRadius: 10, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#475569", marginBottom: 8 }}>WhatsApp Metrics</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {[
                  ["Sent",      selectedEnrollment.waProps?.totalSent],
                  ["Delivered", selectedEnrollment.waProps?.totalDelivered],
                  ["Read",      selectedEnrollment.waProps?.totalRead],
                  ["Replies",   selectedEnrollment.waProps?.totalReplies],
                  ["Clicks",    selectedEnrollment.waProps?.totalClicks],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#94a3b8" }}>{l}</span>
                    <span style={{ fontWeight: 700 }}>{v || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Workflow List ────────────────────────────────────────────
function WorkflowList({ onSelect, onCreate }) {
  const [workflows, setWorkflows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [selectedFolder, setSelectedFolder] = useState("all"); // "all" | "none" | folderId | "archived"
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [folderMenu, setFolderMenu] = useState(null); // wf.id or null
  const [renamingFolder, setRenamingFolder] = useState(null); // { id, name }
  const [renamingWorkflow, setRenamingWorkflow] = useState(null); // { id, name }

  const load = useCallback(async () => {
    setLoading(true);
    const showArchived = selectedFolder === "archived";
    const [wfData, foldersData] = await Promise.all([
      wfFetch(`/workflows${showArchived ? "?archived=true" : ""}`),
      wfFetch("/workflow-folders"),
    ]);
    setWorkflows(wfData.workflows || []);
    setFolders(foldersData.folders || []);
    setLoading(false);
  }, [selectedFolder]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (wf, action) => {
    setActionLoading(prev => ({ ...prev, [wf.id]: true }));
    await wfFetch(`/workflows/${wf.id}/status`, { method: "POST", body: JSON.stringify({ action }) });
    await load();
    setActionLoading(prev => ({ ...prev, [wf.id]: false }));
  };

  const doDuplicate = async (wf) => {
    setActionLoading(prev => ({ ...prev, [wf.id]: true }));
    await wfFetch(`/workflows/${wf.id}/duplicate`, { method: "POST" });
    await load();
    setActionLoading(prev => ({ ...prev, [wf.id]: false }));
  };

  const doDelete = async (wf) => {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    await wfFetch(`/workflows/${wf.id}`, { method: "DELETE" });
    await load();
  };

  const doRenameWorkflow = async () => {
    if (!renamingWorkflow?.name?.trim()) return;
    await wfFetch(`/workflows/${renamingWorkflow.id}`, { method: "PUT", body: JSON.stringify({ name: renamingWorkflow.name.trim() }) });
    setRenamingWorkflow(null);
    await load();
  };

  const doMoveToFolder = async (wf, folderId) => {
    setFolderMenu(null);
    await wfFetch(`/workflows/${wf.id}`, { method: "PUT", body: JSON.stringify({ folderId: folderId || null }) });
    await load();
  };

  const doDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Workflows inside will become uncategorized.`)) return;
    await wfFetch(`/workflow-folders/${folder.id}`, { method: "DELETE" });
    if (selectedFolder === folder.id) setSelectedFolder("all");
    else await load();
  };

  const doRenameFolder = async () => {
    if (!renamingFolder?.name?.trim()) return;
    await wfFetch(`/workflow-folders/${renamingFolder.id}`, { method: "PUT", body: JSON.stringify({ name: renamingFolder.name.trim() }) });
    setRenamingFolder(null);
    await load();
  };

  const nonArchivedWorkflows = workflows.filter(w => w.status !== "archived");
  const allCount  = nonArchivedWorkflows.length;
  const noneCount = nonArchivedWorkflows.filter(w => !w.folderId).length;

  const filtered = workflows.filter(w => {
    if (search && !w.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (selectedFolder === "archived") return true;
    if (selectedFolder === "all")  return true;
    if (selectedFolder === "none") return !w.folderId;
    return w.folderId === selectedFolder;
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>Workflow Automation</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Build and manage WhatsApp automation journeys</div>
        </div>
        <input style={{ ...inp, width: 240 }} placeholder="Search workflows…" value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={onCreate} style={btn("#6366f1")}>+ New Workflow</button>
        <button onClick={load} style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0" }) }}>↻</button>
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Sidebar */}
        <div style={{ width: 220, background: "#fff", borderRight: "1px solid #e2e8f0", display: "flex", flexDirection: "column", overflowY: "auto", flexShrink: 0 }}>
          <div style={{ padding: "14px 14px 6px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em" }}>Folders</div>

          <SidebarItem label="All Workflows" count={allCount} selected={selectedFolder === "all"} onClick={() => setSelectedFolder("all")} icon="⚡" />
          <SidebarItem label="Uncategorized" count={noneCount} selected={selectedFolder === "none"} onClick={() => setSelectedFolder("none")} icon="📋" />

          {folders.map(f => {
            const cnt = nonArchivedWorkflows.filter(w => w.folderId === f.id).length;
            return (
              <div key={f.id} style={{ position: "relative", display: "flex", alignItems: "center", margin: "2px 8px" }}>
                {renamingFolder?.id === f.id ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 4, padding: "4px 6px" }}>
                    <input
                      autoFocus
                      value={renamingFolder.name}
                      onChange={e => setRenamingFolder({ ...renamingFolder, name: e.target.value })}
                      onKeyDown={e => { if (e.key === "Enter") doRenameFolder(); if (e.key === "Escape") setRenamingFolder(null); }}
                      style={{ ...inp, fontSize: 12, padding: "4px 8px", flex: 1 }}
                    />
                    <button onClick={doRenameFolder} style={{ ...btn("#6366f1", { fontSize: 11, padding: "4px 8px" }) }}>✓</button>
                  </div>
                ) : (
                  <>
                    <div onClick={() => setSelectedFolder(f.id)} style={{
                      flex: 1, padding: "8px 14px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                      background: selectedFolder === f.id ? "#eef2ff" : "none",
                      color: selectedFolder === f.id ? "#6366f1" : "#475569",
                      fontWeight: selectedFolder === f.id ? 700 : 400, fontSize: 13,
                    }}
                      onMouseEnter={e => { if (selectedFolder !== f.id) e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseLeave={e => { if (selectedFolder !== f.id) e.currentTarget.style.background = "none"; }}
                    >
                      <span>📁</span>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                      <span style={{ fontSize: 11, background: selectedFolder === f.id ? "#6366f133" : "#f1f5f9", color: selectedFolder === f.id ? "#6366f1" : "#94a3b8", borderRadius: 10, padding: "1px 7px", fontWeight: 700 }}>{cnt}</span>
                    </div>
                    <div style={{ display: "flex", gap: 2, paddingRight: 4 }}>
                      <button onClick={() => setRenamingFolder({ id: f.id, name: f.name })}
                        title="Rename" style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", fontSize: 11, padding: "2px 3px", lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#6366f1"}
                        onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>✏</button>
                      <button onClick={() => doDeleteFolder(f)}
                        title="Delete folder" style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", fontSize: 11, padding: "2px 3px", lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                        onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>✕</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <button onClick={() => setShowCreateFolder(true)}
            style={{ margin: "6px 10px 2px", padding: "7px 12px", background: "none", border: "1px dashed #cbd5e1", borderRadius: 8, cursor: "pointer", fontSize: 12, color: "#64748b", textAlign: "left" }}>
            + New Folder
          </button>

          <div style={{ margin: "10px 14px 4px", borderTop: "1px solid #f1f5f9" }} />
          <SidebarItem label="Archived" count="" selected={selectedFolder === "archived"} onClick={() => setSelectedFolder("archived")} icon="🗄" />
          <div style={{ flex: 1 }} />
        </div>

        {/* Workflow list */}
        <div style={{ flex: 1, overflowY: "auto", background: "#f1f5f9", padding: 24 }}>
          {loading ? (
            <div style={{ color: "#94a3b8", fontSize: 14, textAlign: "center", paddingTop: 60 }}>Loading workflows…</div>
          ) : filtered.length === 0 ? (
            <div style={{ ...card({ textAlign: "center", padding: 60 }) }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>{selectedFolder === "archived" ? "🗄" : "⚡"}</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: "#0f172a", marginBottom: 8 }}>
                {search ? "No workflows match your search" : selectedFolder === "archived" ? "No archived workflows" : "No workflows here"}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 14, marginBottom: 24 }}>
                {!search && selectedFolder === "all" && "Create your first WhatsApp automation workflow"}
                {!search && selectedFolder === "none" && "All workflows are assigned to folders"}
              </div>
              {!search && selectedFolder === "all" && <button onClick={onCreate} style={btn("#6366f1")}>+ Create Workflow</button>}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              {filtered.map(wf => {
                const folderName = folders.find(f => f.id === wf.folderId)?.name;
                return (
                  <div key={wf.id} style={card({ display: "flex", alignItems: "flex-start", gap: 16, cursor: "pointer", transition: "box-shadow .15s", opacity: wf.status === "archived" ? .75 : 1 })}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.12)"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,.07)"}
                    onClick={() => onSelect(wf)}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLORS[wf.status] || "#94a3b8", marginTop: 6, flexShrink: 0 }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        {renamingWorkflow?.id === wf.id ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }} onClick={e => e.stopPropagation()}>
                            <input
                              autoFocus
                              value={renamingWorkflow.name}
                              onChange={e => setRenamingWorkflow({ ...renamingWorkflow, name: e.target.value })}
                              onKeyDown={e => { if (e.key === "Enter") doRenameWorkflow(); if (e.key === "Escape") setRenamingWorkflow(null); }}
                              style={{ ...inp, fontWeight: 700, fontSize: 14, width: 240, padding: "4px 8px" }}
                            />
                            <button onClick={doRenameWorkflow} style={btn("#6366f1", { fontSize: 12, padding: "4px 10px" })}>Save</button>
                            <button onClick={() => setRenamingWorkflow(null)} style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0", fontSize: 12, padding: "4px 10px" }) }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>{wf.name}</div>
                        )}
                        <span style={{ background: (STATUS_COLORS[wf.status] || "#94a3b8") + "22", color: STATUS_COLORS[wf.status] || "#94a3b8", padding: "2px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11 }}>{wf.status}</span>
                        {folderName && <span style={{ background: "#f1f5f9", color: "#64748b", padding: "2px 8px", borderRadius: 6, fontSize: 11 }}>📁 {folderName}</span>}
                      </div>
                      {wf.description && <div style={{ fontSize: 13, color: "#475569", marginBottom: 8 }}>{wf.description}</div>}
                      <div style={{ display: "flex", gap: 20, fontSize: 12, color: "#94a3b8", flexWrap: "wrap" }}>
                        <span>{wf.nodeCount || 0} nodes</span>
                        <span>{wf.audienceLists?.length || 0} audience lists</span>
                        {wf.stats?.enrolled > 0 && <span style={{ color: "#6366f1" }}>👥 {wf.stats.enrolled} enrolled</span>}
                        {wf.stats?.sent > 0 && <span>📤 {wf.stats.sent} sent</span>}
                        {wf.stats?.delivered > 0 && <span style={{ color: "#10b981" }}>✓ {wf.stats.delivered} delivered</span>}
                        {wf.stats?.read > 0 && <span style={{ color: "#06b6d4" }}>👁 {wf.stats.read} read</span>}
                        <span>Updated {new Date(wf.updatedAt).toLocaleDateString("en-IN")}</span>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 6, flexShrink: 0, position: "relative" }} onClick={e => e.stopPropagation()}>
                      {wf.status !== "archived" && (
                        <>
                          {wf.status === "draft" && (
                            <button onClick={() => onSelect(wf, "builder")} style={{ ...btn("#6366f1", { fontSize: 12 }) }}>Edit</button>
                          )}
                          {wf.status === "active" && (
                            <button onClick={() => doAction(wf, "pause")} disabled={actionLoading[wf.id]} style={{ ...btn("#f59e0b", { fontSize: 12 }) }}>Pause</button>
                          )}
                          {wf.status === "paused" && (
                            <button onClick={() => doAction(wf, "resume")} disabled={actionLoading[wf.id]} style={{ ...btn("#10b981", { fontSize: 12 }) }}>Resume</button>
                          )}
                          {["draft", "paused"].includes(wf.status) && (
                            <button onClick={() => onSelect(wf, "activate")} style={{ ...btn("#10b981", { fontSize: 12 }) }}>▶ Activate</button>
                          )}
                          <button onClick={() => doDuplicate(wf)} disabled={actionLoading[wf.id]}
                            style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0", fontSize: 12 }) }}>Copy</button>
                          <button onClick={() => setRenamingWorkflow({ id: wf.id, name: wf.name })}
                            title="Rename workflow"
                            style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0", fontSize: 12 }) }}>✏ Rename</button>

                          {/* Move to folder */}
                          <div style={{ position: "relative" }}>
                            <button onClick={() => setFolderMenu(folderMenu === wf.id ? null : wf.id)}
                              title="Move to folder"
                              style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0", fontSize: 12 }) }}>📁</button>
                            {folderMenu === wf.id && (
                              <div style={{ position: "absolute", right: 0, top: "110%", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.15)", zIndex: 200, minWidth: 190, padding: "6px 0" }}>
                                <div style={{ padding: "6px 14px 4px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" }}>Move to Folder</div>
                                {[{ id: null, name: "No Folder", icon: "📋" }, ...folders.map(f => ({ ...f, icon: "📁" }))].map(f => (
                                  <div key={f.id || "none"} onClick={() => doMoveToFolder(wf, f.id)}
                                    style={{ padding: "8px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: wf.folderId === f.id ? "#6366f1" : "#0f172a", fontWeight: wf.folderId === f.id ? 700 : 400 }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                                    <span>{f.icon}</span> {f.name} {wf.folderId === f.id && <span style={{ marginLeft: "auto" }}>✓</span>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Archive */}
                          {["draft", "paused", "completed"].includes(wf.status) && (
                            <button onClick={() => { if (window.confirm(`Archive "${wf.name}"?`)) doAction(wf, "archive"); }}
                              title="Archive workflow"
                              style={{ ...btn("#f8fafc", { color: "#64748b", border: "1px solid #e2e8f0", fontSize: 12 }) }}>🗄 Archive</button>
                          )}

                          {["draft", "paused", "completed"].includes(wf.status) && (
                            <button onClick={() => doDelete(wf)} style={{ ...btn("#fef2f2", { color: "#ef4444", border: "1px solid #fecaca", fontSize: 12 }) }}>Delete</button>
                          )}
                        </>
                      )}
                      {wf.status === "archived" && (
                        <button onClick={() => doAction(wf, "unarchive")} disabled={actionLoading[wf.id]}
                          style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0", fontSize: 12 }) }}>Unarchive</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showCreateFolder && (
        <CreateFolderModal
          onClose={() => setShowCreateFolder(false)}
          onCreated={() => { setShowCreateFolder(false); load(); }}
        />
      )}

      {/* Overlay to close folder menu */}
      {folderMenu && <div style={{ position: "fixed", inset: 0, zIndex: 199 }} onClick={() => setFolderMenu(null)} />}
    </div>
  );
}

// ─── Folder Sidebar Item ──────────────────────────────────────
function SidebarItem({ label, count, selected, onClick, icon }) {
  return (
    <div onClick={onClick} style={{
      padding: "8px 14px", margin: "2px 8px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
      background: selected ? "#eef2ff" : "none",
      color: selected ? "#6366f1" : "#475569",
      fontWeight: selected ? 700 : 400, fontSize: 13,
    }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "#f8fafc"; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "none"; }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {count !== "" && <span style={{ fontSize: 11, background: selected ? "#6366f133" : "#f1f5f9", color: selected ? "#6366f1" : "#94a3b8", borderRadius: 10, padding: "1px 7px", fontWeight: 700 }}>{count}</span>}
    </div>
  );
}

// ─── Create Folder Modal ──────────────────────────────────────
function CreateFolderModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    setLoading(true);
    const d = await wfFetch("/workflow-folders", { method: "POST", body: JSON.stringify({ name: name.trim() }) });
    setLoading(false);
    if (d.success) onCreated(d.folder);
    else setError(d.error || "Failed to create folder");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 360, boxShadow: "0 20px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: "#0f172a" }}>New Folder</div>
        {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 20 }}>
          <span style={label}>Folder Name *</span>
          <input style={inp} placeholder="e.g. Sales, Support, Marketing" value={name}
            onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && handleCreate()} autoFocus />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...btn("#f1f5f9", { color: "#475569" }) }}>Cancel</button>
          <button onClick={handleCreate} disabled={loading} style={{ ...btn("#6366f1"), opacity: loading ? .7 : 1 }}>
            {loading ? "Creating…" : "Create Folder"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Workflow Modal ────────────────────────────────────
function CreateWorkflowModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    setLoading(true);
    // Create with a default start node
    const startId = "start_" + Date.now();
    const d = await wfFetch("/workflows", {
      method: "POST",
      body: JSON.stringify({
        name: name.trim(),
        description,
        nodes: [{ id: startId, type: "start", position: { x: 250, y: 50 }, data: getDefaultData("start") }],
        edges: [],
        audienceLists: [],
      }),
    });
    setLoading(false);
    if (d.success) onCreated(d.workflow);
    else setError(d.error || "Failed to create workflow");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 440, boxShadow: "0 20px 40px rgba(0,0,0,.2)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: "#0f172a" }}>New Workflow</div>
        {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 14 }}>
          <span style={label}>Workflow Name *</span>
          <input style={inp} placeholder="e.g. Lead Nurture Sequence" value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCreate()} autoFocus />
        </div>
        <div style={{ marginBottom: 20 }}>
          <span style={label}>Description (optional)</span>
          <textarea style={{ ...inp, height: 70, resize: "vertical" }} placeholder="What does this workflow do?"
            value={description} onChange={e => setDescription(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ ...btn("#f1f5f9", { color: "#475569" }) }}>Cancel</button>
          <button onClick={handleCreate} disabled={loading} style={{ ...btn("#6366f1"), opacity: loading ? .7 : 1 }}>
            {loading ? "Creating…" : "Create & Open Builder"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Enrollment History View ──────────────────────────────────
function EnrollmentHistoryView({ workflow, onBack }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const d = await wfFetch(`/workflows/${workflow.id}/enrollments?limit=200`);
    setEnrollments(d.enrollments || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [workflow.id]);

  const loadDetail = async (enr) => {
    const d = await wfFetch(`/workflows/${workflow.id}/enrollments/${enr.id}`);
    setSelected(d.enrollment || enr);
  };

  const filtered = enrollments.filter(e =>
    !search || (e.contactName || "").toLowerCase().includes(search.toLowerCase()) || (e.contactPhone || "").includes(search)
  );

  const evColors = { enrolled: "#10b981", message_sent: "#6366f1", status: "#06b6d4", condition: "#8b5cf6", delay: "#f59e0b", delay_done: "#f59e0b", error: "#ef4444", complete: "#10b981", reply: "#8b5cf6", action: "#06b6d4", wait: "#f59e0b" };

  return (
    <div style={{ height: "100%", overflowY: "auto", background: "#f1f5f9" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13, color: "#475569" }}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{workflow.name} — Enrollment History</div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Complete history of contacts that passed through this workflow</div>
        </div>
        <input style={{ ...inp, width: 220 }} placeholder="Search by name or phone…" value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={load} style={{ ...btn("#f8fafc", { color: "#475569", border: "1px solid #e2e8f0" }) }}>↻ Refresh</button>
      </div>

      <div style={{ padding: 24 }}>
        {loading ? (
          <div style={{ color: "#94a3b8", textAlign: "center", paddingTop: 40 }}>Loading enrollment history…</div>
        ) : (
          <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,.07)" }}>
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, fontSize: 14 }}>
              {filtered.length} enrollment(s)
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Contact", "Phone", "Status", "Current Node", "Started", "Completed", "Actions"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#475569", fontSize: 12 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(e => (
                    <tr key={e.id} style={{ borderBottom: "1px solid #f1f5f9" }}
                      onMouseEnter={ev => ev.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={ev => ev.currentTarget.style.background = ""}>
                      <td style={{ padding: "10px 14px", fontWeight: 600 }}>{e.contactName || "—"}</td>
                      <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>{e.contactPhone}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ background: (STATUS_COLORS[e.status] || "#94a3b8") + "22", color: STATUS_COLORS[e.status] || "#94a3b8", padding: "2px 8px", borderRadius: 6, fontWeight: 700, fontSize: 11 }}>
                          {e.status}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 12 }}>{e.currentNodeId?.split("_")[0] || "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 12 }}>{e.startedAt ? new Date(e.startedAt).toLocaleString("en-IN") : "—"}</td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 12 }}>{e.completedAt ? new Date(e.completedAt).toLocaleString("en-IN") : "—"}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <button onClick={() => loadDetail(e)} style={{ ...btn("#6366f1", { fontSize: 11, padding: "4px 10px" }) }}>View Log</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                  {search ? "No results match your search" : "No contacts have been enrolled in this workflow yet"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: 540, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 40px rgba(0,0,0,.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.contactName || selected.contactPhone}</div>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>{selected.contactPhone} · {selected.status}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#94a3b8" }}>✕</button>
            </div>

            <div style={{ fontWeight: 700, fontSize: 13, color: "#475569", marginBottom: 12 }}>Node Execution Log</div>
            <div style={{ position: "relative", paddingLeft: 20 }}>
              {(selected.timeline || []).map((ev, i) => {
                const c = evColors[ev.type] || "#94a3b8";
                return (
                  <div key={i} style={{ position: "relative", marginBottom: 16, paddingLeft: 20 }}>
                    <div style={{ position: "absolute", left: -20, top: 2, width: 10, height: 10, borderRadius: "50%", background: c }} />
                    {i < (selected.timeline || []).length - 1 && (
                      <div style={{ position: "absolute", left: -16, top: 12, width: 2, height: "calc(100% + 4px)", background: "#e2e8f0" }} />
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: c }}>{ev.action}</div>
                    {ev.detail && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{String(ev.detail).slice(0, 200)}</div>}
                    <div style={{ fontSize: 11, color: "#cbd5e1" }}>{ev.at ? new Date(ev.at).toLocaleString("en-IN") : ""}</div>
                  </div>
                );
              })}
              {!selected.timeline?.length && <div style={{ color: "#94a3b8", fontSize: 13 }}>No execution events recorded</div>}
            </div>

            <div style={{ marginTop: 16, padding: 14, background: "#f8fafc", borderRadius: 10, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#475569", marginBottom: 8 }}>WhatsApp Metrics</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {[["Sent", selected.waProps?.totalSent], ["Delivered", selected.waProps?.totalDelivered], ["Read", selected.waProps?.totalRead], ["Replies", selected.waProps?.totalReplies]].map(([l, v]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#94a3b8" }}>{l}</span>
                    <span style={{ fontWeight: 700 }}>{v || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Workflow Detail / Editor ─────────────────────────────────
function WorkflowDetail({ workflowId, onBack }) {
  const [workflow, setWorkflow] = useState(null);
  const [view, setView] = useState("builder"); // "builder" | "analytics" | "history"
  const [showActivate, setShowActivate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      wfFetch(`/workflows/${workflowId}`),
      wfFetch("/templates"),
    ]).then(([wfData, tData]) => {
      setWorkflow(wfData.workflow);
      setTemplates(tData.templates || []);
      setLoading(false);
    });
  }, [workflowId]);

  if (loading) return <div style={{ padding: 40, color: "#94a3b8" }}>Loading…</div>;
  if (!workflow) return <div style={{ padding: 40, color: "#ef4444" }}>Workflow not found</div>;

  const isActive = ["active", "paused", "completed"].includes(workflow.status);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Sub-tabs */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 24px", display: "flex", alignItems: "center", gap: 0 }}>
        {[
          { id: "builder", label: "🔧 Builder" },
          ...(isActive ? [{ id: "analytics", label: "📊 Analytics" }] : []),
          { id: "history", label: "📋 Enrollment History" },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{
            background: "none", border: "none", borderBottom: `3px solid ${view === t.id ? "#6366f1" : "transparent"}`,
            color: view === t.id ? "#6366f1" : "#94a3b8", fontWeight: 700, padding: "12px 16px", cursor: "pointer", fontSize: 13,
          }}>{t.label}</button>
        ))}
        {["draft", "paused"].includes(workflow.status) && (
          <button onClick={() => setShowActivate(true)} style={{ ...btn("#10b981", { marginLeft: "auto", marginRight: 0, fontSize: 13 }) }}>
            ▶ Activate
          </button>
        )}
        {workflow.status === "active" && (
          <button onClick={async () => {
            await wfFetch(`/workflows/${workflow.id}/status`, { method: "POST", body: JSON.stringify({ action: "pause" }) });
            setWorkflow(prev => ({ ...prev, status: "paused" }));
          }} style={{ ...btn("#f59e0b", { marginLeft: "auto", fontSize: 13 }) }}>⏸ Pause</button>
        )}
      </div>

      {view === "builder" && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <WorkflowBuilder workflow={workflow} templates={templates} onSave={setWorkflow} onBack={onBack} />
        </div>
      )}
      {view === "analytics" && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <WorkflowAnalytics workflow={workflow} onBack={onBack} />
        </div>
      )}
      {view === "history" && (
        <div style={{ flex: 1, overflow: "hidden" }}>
          <EnrollmentHistoryView workflow={workflow} onBack={onBack} />
        </div>
      )}

      {showActivate && (
        <ActivateModal
          workflow={workflow}
          onClose={() => setShowActivate(false)}
          onActivated={(updated) => { setWorkflow(updated); setShowActivate(false); setView("analytics"); }}
        />
      )}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────
export default function WorkflowsTab() {
  const [view, setView] = useState("list"); // "list" | "detail" | "activate"
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [pendingActivate, setPendingActivate] = useState(null);

  const handleSelect = (wf, action) => {
    if (action === "activate") {
      setPendingActivate(wf);
    } else {
      setSelectedId(wf.id);
      setView("detail");
    }
  };

  if (view === "detail" && selectedId) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <WorkflowDetail workflowId={selectedId} onBack={() => { setView("list"); setSelectedId(null); }} />
        {pendingActivate && (
          <ActivateModal
            workflow={pendingActivate}
            onClose={() => setPendingActivate(null)}
            onActivated={() => { setPendingActivate(null); }}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <WorkflowList
        onSelect={handleSelect}
        onCreate={() => setShowCreate(true)}
      />
      {showCreate && (
        <CreateWorkflowModal
          onClose={() => setShowCreate(false)}
          onCreated={(wf) => { setShowCreate(false); setSelectedId(wf.id); setView("detail"); }}
        />
      )}
      {pendingActivate && (
        <ActivateModal
          workflow={pendingActivate}
          onClose={() => setPendingActivate(null)}
          onActivated={() => setPendingActivate(null)}
        />
      )}
    </div>
  );
}
